/**
 * Bilingual fun facts for the teaching-flow creature avatars.
 *
 * Surfaced when the teacher assigns or reveals an avatar to a child.
 * Each fact is one short, child-friendly sentence — designed to be read
 * aloud at the moment of avatar reveal so the teacher has something
 * delightful and ecologically grounded to share alongside the card.
 *
 * The fact picks something *only that creature does* (not generic),
 * tilted toward wonder and care for life rather than trivia.
 */

export interface AvatarFact {
  /** Common name for the creature/force, in both languages. */
  name: { en: string; es: string };
  /** One-sentence fun fact, conversational and shareable with a 3–6 yr old. */
  fact: { en: string; es: string };
}

/**
 * Keyed by the emoji string used in `CREATURE_AVATARS` so a lookup is just
 * `AVATAR_FACTS[emoji]`. Update both files if you add a new avatar.
 */
export const AVATAR_FACTS: Record<string, AvatarFact> = {
  '🐝': {
    name: { en: 'Bee', es: 'Abeja' },
    fact: {
      en: 'Bees can recognize human faces — they see them as patterns, like puzzle pieces.',
      es: 'Las abejas reconocen las caras de las personas — las ven como patrones, como piezas de un rompecabezas.',
    },
  },
  '🐛': {
    name: { en: 'Caterpillar', es: 'Oruga' },
    fact: {
      en: 'A caterpillar has twelve tiny eyes and almost four thousand muscles in its small body.',
      es: 'Una oruga tiene doce ojitos y casi cuatro mil músculos en su pequeño cuerpo.',
    },
  },
  '🦋': {
    name: { en: 'Butterfly', es: 'Mariposa' },
    fact: {
      en: 'Butterflies taste with their feet — when they land on a flower, they already know how it tastes.',
      es: 'Las mariposas saborean con los pies — cuando se posan en una flor, ya saben a qué sabe.',
    },
  },
  '🐞': {
    name: { en: 'Ladybug', es: 'Mariquita' },
    fact: {
      en: 'A ladybug eats up to 5,000 tiny insects in its life — gardeners say she protects the plants.',
      es: 'Una mariquita come hasta 5,000 insectos pequeños en su vida — los jardineros dicen que protege las plantas.',
    },
  },
  '🐌': {
    name: { en: 'Snail', es: 'Caracol' },
    fact: {
      en: 'A snail moves slowly because it carries its house on its back — wherever it goes, it is already home.',
      es: 'Un caracol va despacio porque lleva su casa en la espalda — donde quiera que vaya, ya está en casa.',
    },
  },
  '🦗': {
    name: { en: 'Cricket', es: 'Grillo' },
    fact: {
      en: 'Crickets sing by rubbing their wings together — and the warmer it gets, the faster they sing.',
      es: 'Los grillos cantan frotando sus alas — y cuanto más calor hace, más rápido cantan.',
    },
  },
  '🕷️': {
    name: { en: 'Spider', es: 'Araña' },
    fact: {
      en: 'A spider weaves silk that is stronger than steel — its web can hold a pencil without breaking.',
      es: 'Una araña teje seda más fuerte que el acero — su tela puede sostener un lápiz sin romperse.',
    },
  },
  '🐜': {
    name: { en: 'Ant', es: 'Hormiga' },
    fact: {
      en: 'An ant can carry something fifty times heavier than itself — like you lifting a small car.',
      es: 'Una hormiga puede cargar algo cincuenta veces más pesado que ella — como tú levantando un autito.',
    },
  },
  '🦎': {
    name: { en: 'Lizard', es: 'Lagartija' },
    fact: {
      en: 'When a lizard loses its tail, a brand-new one slowly grows back in its place.',
      es: 'Cuando una lagartija pierde la cola, le crece otra nueva poquito a poco en el mismo lugar.',
    },
  },
  '🐸': {
    name: { en: 'Frog', es: 'Rana' },
    fact: {
      en: 'A frog drinks water through its skin — it does not need to use its mouth at all.',
      es: 'La rana bebe agua a través de la piel — no necesita usar la boca para nada.',
    },
  },
  '🐢': {
    name: { en: 'Turtle', es: 'Tortuga' },
    fact: {
      en: 'A turtle\'s shell has fifty bones inside it — it is part of her, not just a house she carries.',
      es: 'El caparazón de la tortuga tiene cincuenta huesos por dentro — es parte de ella, no solo una casa que carga.',
    },
  },
  '🦆': {
    name: { en: 'Duck', es: 'Pato' },
    fact: {
      en: 'A duck has waterproof feathers that keep it warm and dry, even when it dives under water.',
      es: 'El pato tiene plumas impermeables que lo mantienen caliente y seco, incluso cuando se sumerge en el agua.',
    },
  },
  '🐤': {
    name: { en: 'Chick', es: 'Pollito' },
    fact: {
      en: 'A baby chick can hear its mother\'s voice from inside its egg, before it even hatches.',
      es: 'Un pollito puede escuchar la voz de su mamá desde adentro del huevo, antes de salir.',
    },
  },
  '🦜': {
    name: { en: 'Parrot', es: 'Loro' },
    fact: {
      en: 'A parrot can learn hundreds of words — and some live to be eighty years old.',
      es: 'Un loro puede aprender cientos de palabras — y algunos viven hasta ochenta años.',
    },
  },
  '🦉': {
    name: { en: 'Owl', es: 'Búho' },
    fact: {
      en: 'An owl can turn its head almost all the way around to look behind itself, without moving its body.',
      es: 'El búho puede girar la cabeza casi por completo para mirar hacia atrás, sin mover el cuerpo.',
    },
  },
  '🦅': {
    name: { en: 'Eagle', es: 'Águila' },
    fact: {
      en: 'An eagle can see a tiny mouse on the ground from a kilometer up in the sky.',
      es: 'Un águila puede ver un ratoncito en el suelo desde un kilómetro de altura en el cielo.',
    },
  },
  '🐿️': {
    name: { en: 'Squirrel', es: 'Ardilla' },
    fact: {
      en: 'A squirrel buries thousands of acorns each year — the ones she forgets grow into new trees.',
      es: 'Una ardilla entierra miles de bellotas cada año — las que olvida se convierten en nuevos árboles.',
    },
  },
  '🦔': {
    name: { en: 'Hedgehog', es: 'Erizo' },
    fact: {
      en: 'A hedgehog has about 5,000 spines, and when she\'s happy she rolls and shows her soft belly.',
      es: 'Un erizo tiene unas 5,000 púas, y cuando está feliz se da vuelta y muestra su pancita suave.',
    },
  },
  '🐇': {
    name: { en: 'Rabbit', es: 'Conejo' },
    fact: {
      en: 'When a rabbit is very happy, she jumps in the air and twists — it\'s called a binky.',
      es: 'Cuando un conejo está muy feliz, salta en el aire y gira — se llama "binky" o salto de alegría.',
    },
  },
  '🦇': {
    name: { en: 'Bat', es: 'Murciélago' },
    fact: {
      en: 'A bat sees with sound — it makes tiny clicks and listens to where they bounce back.',
      es: 'El murciélago ve con sonido — hace clics pequeñitos y escucha dónde rebotan.',
    },
  },
  '🐁': {
    name: { en: 'Mouse', es: 'Ratón' },
    fact: {
      en: 'A mouse sings songs to other mice — too high for our ears, but full of feeling.',
      es: 'Un ratón canta canciones a otros ratones — demasiado agudas para nuestros oídos, pero llenas de sentimiento.',
    },
  },
  '🦊': {
    name: { en: 'Fox', es: 'Zorro' },
    fact: {
      en: 'A fox uses her bushy tail like a blanket to wrap around herself when she sleeps in the cold.',
      es: 'El zorro usa su cola peluda como cobija para envolverse cuando duerme en el frío.',
    },
  },
  '🦝': {
    name: { en: 'Raccoon', es: 'Mapache' },
    fact: {
      en: 'A raccoon\'s paws are so sensitive that it explores almost every new thing it touches by feeling it in water.',
      es: 'Las patitas del mapache son tan sensibles que explora casi todo lo que toca sintiéndolo en agua.',
    },
  },
  '🐻': {
    name: { en: 'Bear', es: 'Oso' },
    fact: {
      en: 'A bear sleeps deeply all winter and wakes up in spring lighter and ready to wander.',
      es: 'El oso duerme profundo todo el invierno y se despierta en primavera más liviano y listo para caminar.',
    },
  },
};
